import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  Handshake,
  HeadphonesIcon
} from 'lucide-react';

const stages = [
  {
    id: '01',
    icon: ClipboardCheck,
    titleAr: 'الاستقبال والتقييم',
    titleEn: 'Reception & Assessment',
    descriptionAr: 'فهم الحالة وتقييمها بشكل أولي',
    descriptionEn: 'Understanding the matter and assessing it initially'
  },
  {
    id: '02',
    icon: FileSearch,
    titleAr: 'التشخيص القانوني',
    titleEn: 'Legal Diagnosis',
    descriptionAr: 'تحليل دقيق وتحديد المسار',
    descriptionEn: 'Precise analysis and path definition'
  },
  {
    id: '03',
    icon: Handshake,
    titleAr: 'التعاقد',
    titleEn: 'Contracting',
    descriptionAr: 'تحديد نطاق العمل بوضوح',
    descriptionEn: 'Defining the scope of work clearly'
  },
  {
    id: '04',
    icon: Briefcase,
    titleAr: 'التنفيذ',
    titleEn: 'Execution',
    descriptionAr: 'تنفيذ ومتابعة مستمرة',
    descriptionEn: 'Execution with continuous follow-up'
  },
  {
    id: '05',
    icon: CheckCircle2,
    titleAr: 'الإغلاق',
    titleEn: 'Closure',
    descriptionAr: 'تسليم النتائج النهائية',
    descriptionEn: 'Delivering the final outcomes'
  },
  {
    id: '06',
    icon: HeadphonesIcon,
    titleAr: 'ما بعد الخدمة',
    titleEn: 'Post-Service',
    descriptionAr: 'دعم واستمرار العلاقة',
    descriptionEn: 'Support and relationship continuity'
  }
];

const CustomerJourneySection = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  return (
    <section id="customer-journey" className="py-24 px-4 bg-gradient-to-b from-white via-[#faf8f3] to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14" dir={isArabic ? 'rtl' : 'ltr'}>
          <h2 className="text-4xl sm:text-5xl font-bold text-wazin-navy mb-5">
            {isArabic ? 'رحلة العميل مع وازن' : 'The Client Journey with Wazin'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {isArabic
              ? 'مراحل واضحة ومنهجية لتحقيق أفضل النتائج بكفاءة واحتراف.'
              : 'Clear and structured stages to achieve the best results with efficiency and professionalism.'}
          </p>
        </div>

        <div className="relative">
          <div className="absolute inset-x-0 top-1/2 hidden lg:block h-px bg-gradient-to-r from-transparent via-[#A27849]/35 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 xl:gap-8" dir={isArabic ? 'rtl' : 'ltr'}>
            {stages.map((stage) => (
              <div
                key={stage.id}
                className="group relative bg-white rounded-[28px] p-7 md:p-8 border border-[#A27849]/12 shadow-[0_12px_35px_rgba(15,23,42,0.06)] hover:-translate-y-2 hover:shadow-[0_18px_45px_rgba(15,23,42,0.10)] transition-all duration-300"
              >
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-[28px] bg-gradient-to-r from-[#B58C5A] via-[#A27849] to-[#8C653A]" />

                <div className={`flex items-start gap-4 ${isArabic ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-14 h-14 rounded-full bg-[#A27849] text-white flex items-center justify-center text-lg font-bold shadow-md flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                    {stage.id}
                  </div>

                  <div className="flex-1">
                    <div className={`flex items-center gap-3 mb-3 ${isArabic ? 'flex-row-reverse justify-end' : ''}`}>
                      <div className="w-10 h-10 rounded-xl border border-[#A27849]/15 bg-[#FCF8EF] flex items-center justify-center text-[#A27849] shadow-sm flex-shrink-0 group-hover:bg-[#F7EFD9] transition-colors duration-300">
                        <stage.icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <h3 className="text-2xl font-bold text-wazin-navy leading-snug group-hover:text-[#8C653A] transition-colors duration-300">
                        {isArabic ? stage.titleAr : stage.titleEn}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-base leading-8">
                      {isArabic ? stage.descriptionAr : stage.descriptionEn}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerJourneySection;
